import React from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import {
  TrendingUp, TrendingDown, Trash2, Info, Paperclip,
  ToggleLeft, ToggleRight, Sparkles, ChevronDown, Edit2,
} from 'lucide-react';

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount || 0);

const getConfidenceColor = (confidence) => {
  if (confidence >= 80) return 'bg-emerald-500';
  if (confidence >= 60) return 'bg-yellow-500';
  return 'bg-red-500';
};
const getConfidenceLabel = (confidence) => {
  if (confidence >= 80) return 'High';
  if (confidence >= 60) return 'Medium';
  return 'Low';
};

export const TransactionRow = ({
  txn,
  selected,
  confirmDeleteId,
  categories,
  incomeCategories,
  onToggleSelect,
  onToggleStatus,
  onToggleType,
  onCategoryChange,
  onDelete,
  onOpenIrsInfo,
}) => (
  <div
    className={`p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 hover:bg-muted/50 transition-colors ${
      txn.status === 'draft' ? 'bg-orange-50/50' : ''
    }`}
    data-testid={`txn-${txn.transaction_id}`}
  >
    {/* Mobile: Checkbox + icon row */}
    <div className="flex items-center gap-3 sm:contents">
      <Checkbox
        checked={selected}
        onCheckedChange={() => onToggleSelect(txn.transaction_id)}
        className="flex-shrink-0"
      />
      <div className={`w-10 h-10 flex-shrink-0 rounded-lg flex items-center justify-center ${txn.type === 'income' ? 'bg-emerald-100' : 'bg-red-100'}`}>
        {txn.type === 'income'
          ? <TrendingUp className="w-5 h-5 text-emerald-600" />
          : <TrendingDown className="w-5 h-5 text-red-600" />}
      </div>
      {/* Amount — mobile only */}
      <div className="ml-auto sm:hidden text-right">
        <p className={`font-bold text-lg mono ${txn.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
          {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
        </p>
      </div>
    </div>

    {/* Description + metadata */}
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <span className="font-medium text-sm sm:text-base truncate">{txn.description}</span>
        <Badge
          variant={txn.status === 'draft' ? 'secondary' : 'default'}
          className={`text-xs ${txn.status === 'draft' ? 'bg-orange-200 text-slate-900 font-medium' : ''}`}
        >
          {txn.status}
        </Badge>
        {txn.ai_confidence && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-slate-100 hover:bg-emerald-500 hover:text-white transition-colors group">
                <Sparkles className="w-3 h-3 text-emerald-500 group-hover:text-white" />
                <div className={`w-2 h-2 rounded-full ${getConfidenceColor(txn.ai_confidence)}`} />
                <span className="text-xs font-medium group-hover:text-white">{getConfidenceLabel(txn.ai_confidence)}</span>
              </button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="font-medium">AI Confidence: {txn.ai_confidence}%</p>
              <p className="text-xs text-muted-foreground">Category classification accuracy</p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-600 flex-wrap">
        <span>{txn.date}</span>
        {txn.category && (
          <>
            <span className="hidden sm:inline">•</span>
            <Popover>
              <PopoverTrigger asChild>
                <button className="flex items-center gap-1 px-2 py-0.5 rounded bg-slate-100 hover:bg-emerald-500 hover:text-white transition-colors capitalize group text-xs">
                  {txn.category_source && (
                    <span
                      className={`w-2 h-2 rounded-full flex-shrink-0 ${
                        txn.category_source === 'manual'  ? 'bg-blue-500' :
                        txn.category_source === 'ai'      ? 'bg-emerald-500' :
                        txn.category_source === 'keyword' ? 'bg-amber-400' : 'bg-slate-400'
                      }`}
                      title={
                        txn.category_source === 'manual'  ? 'Manually set' :
                        txn.category_source === 'ai'      ? 'AI categorized' :
                        txn.category_source === 'keyword' ? 'Auto-matched (keyword)' : 'Imported'
                      }
                      data-testid="category-source-dot"
                    />
                  )}
                  <span className="group-hover:text-white truncate max-w-[120px]">{txn.category.replace(/_/g, ' ')}</span>
                  <ChevronDown className="w-3 h-3 group-hover:text-white flex-shrink-0" />
                  {txn.user_override && <Edit2 className="w-3 h-3 text-blue-500 group-hover:text-white" />}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-2 bg-white border-slate-200" align="start">
                <p className="text-xs font-medium text-slate-500 mb-2 px-2">Change Category</p>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {(txn.type === 'income' ? incomeCategories : categories).map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => onCategoryChange(txn.transaction_id, cat.id)}
                      className={`w-full text-left px-2 py-1.5 rounded text-sm transition-colors ${
                        txn.category === cat.id
                          ? 'bg-emerald-500 text-white font-medium'
                          : 'text-slate-700 hover:bg-emerald-500 hover:text-white'
                      }`}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </>
        )}
        {txn.irs_line_info && (
          <>
            <span>•</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="text-emerald-600 hover:bg-emerald-500 hover:text-white px-1 rounded transition-colors flex items-center gap-1"
                  onClick={() => onOpenIrsInfo(txn.irs_line_info)}
                >
                  <Info className="w-3 h-3" />
                  {txn.irs_line_info.code}
                </button>
              </TooltipTrigger>
              <TooltipContent><p>{txn.irs_line_info.name}</p></TooltipContent>
            </Tooltip>
          </>
        )}
        {txn.receipt_id && <Paperclip className="w-3 h-3" />}
      </div>
    </div>

    {/* Amount — desktop */}
    <div className="hidden sm:block text-right flex-shrink-0">
      <p className={`font-bold mono ${txn.type === 'income' ? 'text-emerald-600' : 'text-red-600'}`}>
        {txn.type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
      </p>
      {txn.type === 'expense' && txn.deductible_percentage < 100 && (
        <p className="text-xs text-muted-foreground">{txn.deductible_percentage}% deductible</p>
      )}
    </div>

    {/* Action buttons */}
    <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost" size="icon"
            onClick={() => onToggleType(txn.transaction_id, txn.type)}
            className={`h-8 w-8 sm:h-10 sm:w-10 ${txn.type === 'income' ? 'text-emerald-500 hover:text-emerald-600' : 'text-red-500 hover:text-red-600'}`}
            data-testid={`toggle-type-${txn.transaction_id}`}
          >
            {txn.type === 'income' ? <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5" /> : <TrendingDown className="w-4 h-4 sm:w-5 sm:h-5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent><p>Switch to {txn.type === 'income' ? 'Expense' : 'Income'}</p></TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost" size="icon"
            onClick={() => onToggleStatus(txn)}
            className={`h-8 w-8 sm:h-10 sm:w-10 ${txn.status === 'draft' ? 'text-orange-500 hover:text-orange-600' : 'text-emerald-500 hover:text-emerald-600'}`}
            data-testid={`toggle-status-${txn.transaction_id}`}
          >
            {txn.status === 'draft' ? <ToggleLeft className="w-4 h-4 sm:w-5 sm:h-5" /> : <ToggleRight className="w-4 h-4 sm:w-5 sm:h-5" />}
          </Button>
        </TooltipTrigger>
        <TooltipContent><p className="text-xs">{txn.status === 'draft' ? 'Mark Final' : 'Mark Draft'}</p></TooltipContent>
      </Tooltip>

      <Button
        variant="ghost" size="icon"
        onClick={(e) => { e.stopPropagation(); onDelete(txn.transaction_id); }}
        className={`h-8 w-8 sm:h-10 sm:w-10 ${confirmDeleteId === txn.transaction_id ? 'bg-red-500 text-white hover:bg-red-600' : 'text-muted-foreground hover:text-destructive'}`}
        data-testid={`delete-txn-${txn.transaction_id}`}
      >
        <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </Button>
    </div>
  </div>
);
